// Client for Ads Health.
//
// Read-only over Amazon. The only writes are the seller's own dismissals and
// the collect call, which pulls the facts reports do not carry — eligibility,
// budget usage, stock and today's prices — and stores them.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/ads-health`;

const headers = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export class NotConnectedError extends Error {
  constructor(message) {
    super(message || 'Amazon Ads not connected');
    this.notConnected = true;
  }
}

const call = async (url, options) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  // Unavailable is an answer, not an error. It comes back with success and a
  // reason rather than rendering an account that does not exist as a score.
  if (data.connected === false) throw new NotConnectedError(data.reason);
  return data;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.set(k, v);
  });
  return search.toString();
};

export const getOverview = (token, params) =>
  call(`${BASE}/overview?${query(params)}`, { headers: headers(token) });
export const getScore = (token, params) =>
  call(`${BASE}/score?${query(params)}`, { headers: headers(token) });
export const getFindings = (token, params) =>
  call(`${BASE}/findings?${query(params)}`, { headers: headers(token) });
export const getWaste = (token, params) =>
  call(`${BASE}/waste?${query(params)}`, { headers: headers(token) });
export const getAudit = (token, params) =>
  call(`${BASE}/audit?${query(params)}`, { headers: headers(token) });
export const getGaps = (token, params) =>
  call(`${BASE}/gaps?${query(params)}`, { headers: headers(token) });
export const getReadiness = (token, params) =>
  call(`${BASE}/readiness?${query(params)}`, { headers: headers(token) });
export const getDiagnosis = (token, params) =>
  call(`${BASE}/diagnosis?${query(params)}`, { headers: headers(token) });

export const dismissFinding = (token, id, note) =>
  call(`${BASE}/findings/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST', headers: headers(token), body: JSON.stringify({ note }),
  });
export const restoreFinding = (token, id) =>
  call(`${BASE}/findings/${encodeURIComponent(id)}/restore`, {
    method: 'POST', headers: headers(token),
  });
// The audit PDF. Fetched as a blob rather than navigated to, because the route
// needs the bearer token and a plain <a href> cannot carry one.
export const getAuditPdf = async (token, params) => {
  const res = await fetch(`${BASE}/audit.pdf?${query(params)}`, { headers: headers(token) });
  if (!res.ok) throw new Error(`Could not build the audit (${res.status})`);
  return res.blob();
};

export const collect = (token, params) =>
  call(`${BASE}/collect?${query(params)}`, { method: 'POST', headers: headers(token) });
